<template>
<div class="facilities-table-detail-row">
    <div class="flex">
        <div class="flex">
            <strong>Address:</strong>
            <div class="flex fd-c ml-3">
                <span>{{ rowData.address }}</span>
                <span>{{ rowData.city }}, {{ rowData.state }} {{ rowData.zip_code }}</span>
                <a class="d-ib fs-09" :href="getAddressMapUrl()" target="_blank">
                    Open in Google Maps
                    <span><i class="material-icons">launch</i></span>
                </a>
            </div>
        </div>

        <div class="as-c gray-divider ml-5 mr-5">&nbsp;</div>

        <div class="flex">
            <strong>Contact:</strong>
            <div class="flex fd-c ml-3">
                <span>{{ rowData.contact_name }}</span>
                <a :href="'mailto:' + rowData.email">{{ rowData.email }}</a>
                <span v-html="rowData.phone"></span>
                <span v-if="rowData.fax !== null" v-html="rowData.fax"></span>
            </div>
        </div>

        <div class="as-c gray-divider ml-5 mr-5">&nbsp;</div>

        <div class="flex">
            <strong class="mr-3">Additional Contacts:</strong>

            <div v-if="rowData.additional_contacts.length === 0">
                <span class="gray-message">--</span>
            </div>

            <div v-else-if="rowData.additional_contacts.length === 1">
                <button type="button" class="text" @click.stop="onViewContacts">
                    <i class="material-icons">contact_phone</i>

                    <span class="ml-1">View 1 contact</span>
                </button>
            </div>

            <div v-else>
                <button type="button" class="text" @click.stop="onViewContacts">
                    <i class="material-icons">contacts</i>

                    <span class="ml-1">View {{ rowData.additional_contacts.length }} contacts</span>
                </button>
            </div>
        </div>

        <div class="as-c gray-divider ml-5 mr-5">&nbsp;</div>

        <div class="flex">
            <strong>Actions:</strong>
            <div class="flex ai-fs ml-3">
                <div class="flex ai-fs fd-c">
                    <router-link :to="`/tenant-list?facility_number=${rowData.facility_number}`">
                        <i class="material-icons">people</i>
                        <span class="ml-1">View Tenants</span>
                    </router-link>

                    <button class="text" type="button" @click="onOpenDownloadSiteLinkLedgerModal">
                        <i class="material-icons">save_alt</i>
                        <span class="ml-1">Download SiteLink Rent Roll</span>
                    </button>

                    <button class="text" type="button" @click="onOpenDownloadStorEdgeLedgerModal">
                        <i class="material-icons">save_alt</i>
                        <span class="ml-1">Download StorEdge Ledger</span>
                    </button>

                    <button class="text danger" type="button" @click="onArchiveFacility">
                        <i class="material-icons">assignment_turned_in</i>
                        <span class="ml-1">Archive Facility</span>
                    </button>
                </div>
            </div>
        </div>

        <div class="as-c gray-divider ml-5 mr-5">&nbsp;</div>

        <div class="flex">
            <strong>Other:</strong>

            <div class="flex ai-fs ml-3">
                <div class="flex ai-fs fd-c">
                    <router-link :to="`/manage-cycles?facility_number=${rowData.facility_number}`">
                        <i class="material-icons">view_module</i>
                        <span class="ml-1">View Cycles</span>
                    </router-link>
                </div>
            </div>
        </div>
    </div>

    <ViewContactsModal
        v-if="view_contacts_modal_open"
        :facility="rowData"
        @close="onViewContactsModalClose" />
</div>
</template>

<script>
import Vue from "vue"
import dayjs from "dayjs"
import http from "@/http"
import { formatPhone } from "../utilities/columns"
import DownloadLedgerModal from "./list-facilities-page/DownloadLedgerModal.vue"
import ViewContactsModal from "./list-facilities-page/ViewContactsModal.vue"

export default {
    name: "facilities-table-detail-row",

    components: {
        ViewContactsModal
    },

    props: {
        rowData: {
            required: true,
            type: Object
        },

        rowIndex: {
            type: Number
        }
    },

    data () {
        return {
            formatPhoneFunc: formatPhone.bind(this),
            view_contacts_modal_open: false
        }
    },

    methods: {
        /**
         * Get a Google Maps URL for an address in facility data.
         *
         * @param object data Facility data object
         *
         * @return string
         */
        getAddressMapUrl () {
            const base = "https://www.google.com/maps/place"
            const query = `${this.rowData.address.replace(/\s/g, "+")},+${this.rowData.city.replace(/\s/g, "+")},+${this.rowData.state}+${this.rowData.zip_code}`

            return `${base}/${query}`
        },

        async onArchiveFacility () {
            const message = `Are you sure you want to archive ${this.rowData.facility_number}?`

            if (confirm(message)) {
                this.$loadingManager.show(`Archiving ${this.rowData.facility_number}...`)

                try {
                    const { data: { filename: fileKey } } = await http.post(`/api/facilities/${this.rowData.id}/archive`)

                    let downloadUrl = `${http.defaults.baseURL}/api/exports/download`
                    downloadUrl += `?file_key=${fileKey}&filename=${this.rowData.facility_number}_${dayjs().format("YYYY-MM-DD")}_export`

                    window.open(downloadUrl, "_blank")

                    this.$eventManager.emit("facility-archive-success")
                } catch (ex) {
                    if (ex === undefined) {
                        this.$modalManager.add({ state: "bad", header: "Error", body: "Unexpected error!" })
                    } else if (ex.message) {
                        this.$modalManager.add({ state: "bad", header: "Error", body: ex.message })
                    } else {
                        this.$modalManager.add({ state: "bad", header: "Error", body: ex })
                    }
                } finally {
                    this.$loadingManager.hide()
                }
            }
        },

        onOpenDownloadSiteLinkLedgerModal () {
            const componentConstructor = Vue.extend(DownloadLedgerModal)

            this.$eventManager.emit("show-modal", {
                component: componentConstructor,
                props: {
                    facility: Object.assign({}, this.rowData),
                    software: "sitelink"
                }
            })
        },

        onOpenDownloadStorEdgeLedgerModal () {
            const componentConstructor = Vue.extend(DownloadLedgerModal)

            this.$eventManager.emit("show-modal", {
                component: componentConstructor,
                props: {
                    facility: Object.assign({}, this.rowData),
                    software: "storedge"
                }
            })
        },

        onViewContacts () {
            this.view_contacts_modal_open = true
        },

        onViewContactsModalClose () {
            this.view_contacts_modal_open = false
        }
    }
}
</script>



// WEBPACK FOOTER //
// src/components/FacilitiesTableDetailRow.vue